// src/components/Forms/DonationForm.tsx
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { DonationFormData } from '../../types';

const DonationForm: React.FC = () => {
  const [donationType, setDonationType] = useState<'one-time' | 'monthly'>('one-time');
  const [selectedAmount, setSelectedAmount] = useState<number>(50);

  const { register, handleSubmit, formState: { errors } } = useForm<DonationFormData>();

  const presetAmounts = [25, 50, 100, 250, 500];

  const onSubmit = (data: DonationFormData) => {
    // In a real application, this would integrate with PayPal/Stripe
    console.log('Donation data:', {
      ...data,
      amount: selectedAmount,
      is_recurring: donationType === 'monthly'
    });
    
    alert(`Thank you for your ${donationType} donation of $${selectedAmount}! This would integrate with payment gateway in production.`);
  };

  return (
    <div className="row justify-content-center">
      <div className="col-lg-8">
        <div className="card shadow">
          <div className="card-body p-4 p-md-5">
            <h3 className="card-title text-center mb-4">Make a Donation</h3>
            
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              Your donation supports sustainable food security and climate resilience projects across Africa.
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Donation Type */}
              <div className="mb-4">
                <label className="form-label fw-bold">Donation Type</label>
                <div className="row">
                  <div className="col-6">
                    <div className="form-check card border p-3 h-100">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="donationType"
                        id="oneTime"
                        checked={donationType === 'one-time'}
                        onChange={() => setDonationType('one-time')}
                      />
                      <label className="form-check-label fw-bold" htmlFor="oneTime">
                        One-time Donation
                      </label>
                      <small className="text-muted d-block mt-1">
                        Make a single donation today
                      </small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-check card border p-3 h-100">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="donationType"
                        id="monthly"
                        checked={donationType === 'monthly'}
                        onChange={() => setDonationType('monthly')}
                      />
                      <label className="form-check-label fw-bold" htmlFor="monthly">
                        Monthly Donation
                      </label>
                      <small className="text-muted d-block mt-1">
                        Support us consistently every month
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Amount Selection */}
              <div className="mb-4">
                <label className="form-label fw-bold">Select Amount (USD)</label>
                <div className="row g-2 mb-3">
                  {presetAmounts.map((amount) => (
                    <div key={amount} className="col-4 col-sm-2">
                      <button
                        type="button"
                        className={`btn w-100 ${
                          selectedAmount === amount ? 'btn-success' : 'btn-outline-success'
                        }`}
                        onClick={() => setSelectedAmount(amount)}
                      >
                        ${amount}
                      </button>
                    </div>
                  ))}
                  <div className="col-4 col-sm-4">
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Other"
                        value={selectedAmount}
                        onChange={(e) => setSelectedAmount(Number(e.target.value))}
                        min="1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Donor Information */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    id="name"
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    id="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                </div>
              </div>

              {/* Impact Display */}
              <div className="alert alert-success">
                <h6 className="fw-bold">Your Impact:</h6>
                <p className="mb-1">
                  <strong>${selectedAmount}</strong> {donationType === 'monthly' ? 'monthly' : 'one-time'} can provide:
                </p>
                <ul className="mb-0 small">
                  <li>Plant {Math.floor(selectedAmount / 5)} trees</li>
                  <li>Train {Math.floor(selectedAmount / 25)} women in sustainable farming</li>
                  <li>Support {Math.floor(selectedAmount / 50)} youth in climate action programs</li>
                </ul>
              </div>

              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-success btn-lg py-3 fw-bold"
                >
                  {donationType === 'monthly' ? 'Donate Monthly' : 'Donate Now'} - ${selectedAmount}
                </button>
              </div>

              <div className="text-center mt-3">
                <small className="text-muted">
                  <i className="bi bi-shield-check me-1"></i>
                  Secure payment processing
                </small>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationForm;